import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faShieldAlt, faHome, faArrowLeft, faUser, faEnvelope, faExclamationTriangle, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { LoadingSpinnerComponent } from '../../shared/loading-spinner/loading-spinner.component';
import { LoadingService } from '../../services/loading.service';

@Component({
  selector: 'app-access-denied',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, LoadingSpinnerComponent],
  templateUrl: './access-denied.component.html',
  styleUrls: ['./access-denied.component.css']
})
export class AccessDeniedComponent implements OnInit {
  // FontAwesome icons
  faShieldAlt = faShieldAlt;
  faHome = faHome;
  faArrowLeft = faArrowLeft;
  faUser = faUser;
  faEnvelope = faEnvelope;
  faExclamationTriangle = faExclamationTriangle;
  faSignOutAlt = faSignOutAlt;

  // User information
  userEmail: string | null = null;
  userName: string | null = null;
  isLoggedIn: boolean = false;

  constructor(
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
    private msal: MsalService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    
    // Ensure loading is hidden after component loads
    setTimeout(() => {
      this.loadingService.forceHide();
    }, 100);
  }

  private loadUserInfo(): void {
    const account = this.msal.instance.getActiveAccount() ?? 
                   this.msal.instance.getAllAccounts()[0] ?? 
                   null;

    if (account) {
      this.isLoggedIn = true;
      this.userEmail = this.getEmailFromAccount(account);
      this.userName = this.getUserNameFromAccount(account);
    }
  }

  private getEmailFromAccount(account: AccountInfo): string | null {
    const claims = account.idTokenClaims as any | undefined;
    const fromClaims = 
      (Array.isArray(claims?.emails) && claims.emails[0]) ||
      claims?.email ||
      claims?.preferred_username ||
      null;

    return (fromClaims || account.username || null) ?? null;
  }

  private getUserNameFromAccount(account: AccountInfo): string | null {
    const claims = account.idTokenClaims as any | undefined;
    return claims?.name || claims?.given_name || this.userEmail || 'User';
  }

  goBack(): void {
    this.location.back();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  logout(): void {
    this.msal.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    });
  }
}

export const onlyLoggedInCanAccess: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  
  const msalService = inject(MsalService);
  const account = msalService.instance.getActiveAccount() ?? 
                 msalService.instance.getAllAccounts()[0] ?? 
                 null;
  if (account) {
    return true;
  } else {
    const router = inject(Router);
    router.navigate(['/']);
    return false;
  }  
};
